/**
 * One citation of a thesis (#765, spec §8.2): «▶ 4:32 · Agent» plays the
 * moment; the quote itself is shown under it. Modelled on the key-moment
 * button in `KeyMomentsSection`, a new look; the timecode is the transcript's
 * own format (`formatDuration`), not a second one (owner's review, 17.09).
 *
 * - `startSec` null: grounding failed (`ungrounded`) or matched several
 *   places (`ambiguous`) — no timecode to play, the tag says which;
 * - `quote === ''`: PII minimization emptied it at write time (§6.2) — the
 *   timecode stays playable;
 * - `recordDeleted`: the record is gone or no longer visible — nothing to
 *   click;
 * - `otherRecord`: an answer about a deal quotes several conversations, and
 *   a timecode alone does not say which one. A quote from a record other
 *   than the one on screen says «another call ↗» — the click opens it
 *   (#765 Ф2).
 */

import { Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AssistantCitation } from '@aica/api-types/schemas';
import { formatDuration } from '@aica/core';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface QuoteButtonProps {
  citation: AssistantCitation;
  onPlay: (citation: AssistantCitation) => void;
  /** The quote is from a record other than the one on screen. */
  otherRecord?: boolean;
}

export function QuoteButton({ citation, onPlay, otherRecord = false }: QuoteButtonProps) {
  const { t } = useTranslation('assistant');
  const speaker = t(`answer.speaker.${citation.speaker}`);
  const playable = citation.startSec !== null && !citation.recordDeleted;
  const timecode = formatDuration((citation.startSec ?? 0) * 1000);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {citation.recordDeleted ? (
          <span className="font-mono text-fg-muted">{t('answer.recordDeleted')}</span>
        ) : playable ? (
          <button
            type="button"
            onClick={() => onPlay(citation)}
            aria-label={
              otherRecord
                ? t(`answer.openRecordAt.${citation.recordType}`, { time: timecode })
                : t('answer.playAt', { time: timecode })
            }
            className={cn(
              'inline-flex items-center gap-1 rounded-button font-mono transition-colors',
              'text-ai hover:text-ai-hover underline underline-offset-2',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
          >
            <Play className="h-3 w-3" aria-hidden="true" />
            {timecode}
            <span className="text-fg-muted no-underline">· {speaker}</span>
            {otherRecord && (
              <span
                className="text-fg-muted no-underline"
                data-testid="assistant-quote-other-record"
              >
                · {t(`answer.otherRecord.${citation.recordType}`)} ↗
              </span>
            )}
          </button>
        ) : otherRecord ? (
          // No timecode to play, but still a quote from ANOTHER conversation:
          // without the mark it reads as the call on screen, and the user
          // would attribute its words there (#1041 review). The click opens
          // that record from its start.
          <button
            type="button"
            onClick={() => onPlay(citation)}
            aria-label={t(`answer.openRecord.${citation.recordType}`)}
            className={cn(
              'inline-flex items-center gap-1 rounded-button font-mono transition-colors',
              'text-fg-muted hover:text-fg underline underline-offset-2',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
          >
            {speaker}
            <span data-testid="assistant-quote-other-record">
              · {t(`answer.otherRecord.${citation.recordType}`)} ↗
            </span>
          </button>
        ) : (
          <span className="font-mono text-fg-muted">{speaker}</span>
        )}
        {citation.grounding === 'ungrounded' && (
          <Badge variant="warning" size="xs">
            {t('answer.ungrounded')}
          </Badge>
        )}
        {citation.grounding === 'ambiguous' && (
          <Badge variant="default" size="xs">
            {t('answer.ambiguous')}
          </Badge>
        )}
      </div>
      <blockquote className="border-l-2 border-ai pl-2 text-sm text-fg-muted whitespace-pre-line">
        {citation.quote === '' ? (
          <span className="italic">{t('answer.quoteHidden')}</span>
        ) : (
          citation.quote
        )}
      </blockquote>
    </div>
  );
}
